import {
  SnackbarVariant,
  Text,
  TextSize,
  TextWeight,
  makeStyles,
  useSnackbar
} from '@focaldata/cin-ui-components'
import { Fragment, ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isGlobalAudienceEnabled } from '../../../constants/featureFlags'
import { Survey } from '../../../data/gql-gen/dashboard/graphql'
import { useMarketsSpecification } from '../../../hooks/audience/useMarketsSpecification'
import { useProjectId } from '../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../modules/Project/Project.hooks'
import { useBaseMarketLabel } from '../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { parseMarketName } from '../../../utils/marketFlags'
import {
  AddMarketDialog,
  AddMarketDialogData
} from './ProjectPage/AddMarketDialog'
import { useAddMarket } from './ProjectPage/ProjectPage.hooks'
import { isProjectInProgress } from './markets.utils'

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    borderBottom: '1px solid #E5EAF5',
    margin: '16px 0',
    // scroll the tabs horizontally instead of overflowing the page width
    maxWidth: '100%',
    flexWrap: 'nowrap',
    overflowX: 'auto'
  },
  // reset so a native (accessible) <button> can be styled as a tab
  tab: {
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '8px 14px',
    marginBottom: -1,
    cursor: 'pointer',
    color: '#42526E',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    // keep each tab on one line and stop them being squashed when scrolling
    flexShrink: 0,
    whiteSpace: 'nowrap'
  },
  tabActive: {
    color: 'var(--primary)',
    borderBottom: '2px solid var(--primary)'
  },
  addTab: { color: 'var(--primary)' },
  flag: { fontSize: 16, lineHeight: 1 },
  // Small read-only "Base" pill marking the base market on the tab — the base is
  // set on the Project page (a wave's in Market settings); here it's only an
  // indicator.
  baseTag: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: 'var(--primary)',
    border: '1px solid var(--primary)',
    borderRadius: 4,
    padding: '2px 4px'
  },
  empty: { padding: '24px 0', color: '#6B7280' }
})

/**
 * Shared shell for the per-market step screens: title, market tabs and the
 * + Add Market dialog. The selected market comes from `:surveyId`, and
 * `renderEditor` is remounted per market so its data fetching re-runs.
 *
 * The step chrome is passed in: both the multi-market flow and a wave's flow
 * use this, with different breadcrumbs and route roots.
 */
export const MarketTabsLayout = ({
  breadcrumb,
  title,
  marketBasePath,
  markets,
  waveId,
  emptyText,
  renderEditor
}: {
  breadcrumb: ReactNode
  title: string
  // Absolute path the selected market's surveyId is appended to.
  marketBasePath: string
  // A project's markets, or one wave's.
  markets: Survey[]
  // Set on a wave's steps, so a market added here is attached to that wave.
  waveId?: string
  emptyText: string
  renderEditor: () => ReactNode
}) => {
  const { classes, cx } = useStyles()
  const projectId = useProjectId()
  const { surveyId: selectedSurveyId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { project } = useGetProjectByProjectId()
  const [isAddMarketOpen, setIsAddMarketOpen] = useState(false)
  // Wraps the whole bulk-add loop so the dialog shows a single loading state
  // across every market being created.
  const [isBulkAdding, setIsBulkAdding] = useState(false)
  // addMarket appends the new market to the cached project itself, so the tab
  // list updates without a refetch.
  const { addMarket } = useAddMarket(projectId, waveId)
  const baseMarket = useBaseMarketLabel()

  // Mark the current base market on the tabs (behind the GlobalAudience flag).
  // questionnaireAggV2 is a project-level aggregate, so any market's surveyId
  // surfaces every market's isBase; skip the query entirely when the flag is off.
  const globalAudienceEnabled = isGlobalAudienceEnabled()
  const { isBaseBySurveyId } = useMarketsSpecification(
    globalAudienceEnabled ? markets[0]?.surveyId : undefined
  )

  // Land on the first market when none is selected.
  useEffect(() => {
    if (!selectedSurveyId && markets.length > 0) {
      navigate(`${marketBasePath}/${markets[0].surveyId}`, {
        replace: true
      })
    }
  }, [selectedSurveyId, markets, navigate, marketBasePath])

  const handleAddMarkets = async (
    rows: AddMarketDialogData[]
  ): Promise<number> => {
    setIsBulkAdding(true)
    // How many markets were created before the loop stopped. Returned to the
    // dialog so it can drop exactly those rows and keep the rest for retry.
    let created = 0
    try {
      let lastSurveyId: string | undefined
      // Sequentially: each addMarket primes its fieldwork cache and appends to
      // the cached project before the next, so the tab list picks up each new
      // market as it is created. A parallel run would race those cache writes.
      // The same country may legitimately be added more than once (e.g. a
      // national market plus a regional boost), so rows are not de-duplicated
      // against the existing markets. addMarket rolls back and throws if a
      // market's fieldwork can't be created, so a failure stops the loop here
      // rather than leaving a half-created market.
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        lastSurveyId = await addMarket(row)
        created += 1
      }
      setIsAddMarketOpen(false)
      // The last added market becomes the active tab.
      if (lastSurveyId) {
        navigate(`${marketBasePath}/${lastSurveyId}`)
      }
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to add market',
        { variant: SnackbarVariant.Error }
      )
    } finally {
      setIsBulkAdding(false)
    }
    return created
  }

  return (
    <div>
      {breadcrumb}

      <div className={classes.page}>
        <Text size={TextSize.xl} weight={TextWeight.Bold}>
          {title}
        </Text>

        <div className={classes.tabs}>
          {markets.map((survey) => {
            // The base market's label/flag come from the resolved base-market
            // info: a master created before masters were named as markets is
            // named after its project, so its name can't be parsed for them.
            const { flag, label } =
              baseMarket && survey.surveyId === baseMarket.surveyId
                ? baseMarket
                : parseMarketName(survey.name)
            const isActive = survey.surveyId === selectedSurveyId
            const isBase = isBaseBySurveyId.get(survey.surveyId) ?? false
            return (
              <button
                key={survey.surveyId}
                type="button"
                className={cx(classes.tab, isActive && classes.tabActive)}
                onClick={() => navigate(`${marketBasePath}/${survey.surveyId}`)}
              >
                {flag && <span className={classes.flag}>{flag}</span>}
                <Text
                  size={TextSize.ms}
                  weight={isActive ? TextWeight.SemiBold : TextWeight.Regular}
                >
                  {label}
                </Text>
                {globalAudienceEnabled && isBase && (
                  <span className={classes.baseTag}>Base</span>
                )}
              </button>
            )
          })}
          <button
            type="button"
            className={cx(classes.tab, classes.addTab)}
            onClick={() => setIsAddMarketOpen(true)}
          >
            <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
              + Add markets
            </Text>
          </button>
        </div>

        {markets.length === 0 ? (
          <div className={classes.empty}>
            <Text size={TextSize.ms}>{emptyText}</Text>
          </div>
        ) : selectedSurveyId ? (
          // Keyed on surveyId so the editor (and its data fetching/cache
          // handling) remounts cleanly when switching markets.
          <Fragment key={selectedSurveyId}>{renderEditor()}</Fragment>
        ) : null}
      </div>

      <AddMarketDialog
        open={isAddMarketOpen}
        loading={isBulkAdding}
        projectInProgress={isProjectInProgress(project)}
        onAdd={handleAddMarkets}
        onClose={() => setIsAddMarketOpen(false)}
      />
    </div>
  )
}
